/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
(() => {
  const COUPON_CODE = 'PRIMEIRA10';
  const BUTTON_SELECTOR = 'a[href="#popup-primeira10"]';

  async function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const temporaryInput = document.createElement('textarea');
    temporaryInput.value = text;
    temporaryInput.style.position = 'fixed';
    temporaryInput.style.opacity = '0';
    document.body.appendChild(temporaryInput);
    temporaryInput.select();

    const copied = document.execCommand('copy');
    temporaryInput.remove();

    if (!copied) {
      throw new Error('Não foi possível copiar o cupom.');
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);

    if (!button) return;

    event.preventDefault();

    if (button.dataset.copying === 'true') return;

    const originalText = button.textContent.trim();
    button.dataset.copying = 'true';

    try {
      await copyToClipboard(COUPON_CODE);

      button.textContent = 'CUPOM COPIADO ✓';
      button.setAttribute(
        'aria-label',
        `Cupom ${COUPON_CODE} copiado para a área de transferência`
      );

      document.dispatchEvent(
        new CustomEvent('fj:popup:coupon-copied', {
          detail: {
            coupon: COUPON_CODE
          }
        })
      );

      window.setTimeout(() => {
        button.textContent = originalText;
        button.removeAttribute('aria-label');
        delete button.dataset.copying;
      }, 2500);
    } catch (error) {
        document.dispatchEvent(
  new CustomEvent('fj:popup:coupon-copy-error', {
    detail: {
      coupon: COUPON_CODE,
      message:
        error?.message || 'Não foi possível copiar o cupom.'
    }
  })
);
      button.textContent = `COPIE: ${COUPON_CODE}`;

      window.setTimeout(() => {
        button.textContent = originalText;
        delete button.dataset.copying;
      }, 3500);
    }
  });
})();
(() => {
  const CHIP_DELAY = 8000;
  const MOBILE_BREAKPOINT = 749;
  const CHIP_CLASS = 'fj-mobile-coupon-chip';

  function isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  }

  function findCouponPopup() {
    const popups = document.querySelectorAll('pop-up');

    return Array.from(popups).find((popup) =>
      popup.querySelector(
        'a[href*="popup-primeira10"], a[href*="PRIMEIRA10"]'
      )
    );
  }

  function addStyles() {
    if (document.getElementById('fj-mobile-coupon-chip-style')) return;

    const style = document.createElement('style');
    style.id = 'fj-mobile-coupon-chip-style';

    style.textContent = `
      .${CHIP_CLASS} {
        position: fixed !important;
       left: 14px !important;
right: auto !important;
bottom: calc(20px + env(safe-area-inset-bottom)) !important;
        z-index: 2147483000 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 44px !important;
        padding: 12px 18px !important;
        border: 1px solid rgba(255, 255, 255, 0.35) !important;
        border-radius: 999px !important;
        background: #8d5b49 !important;
        color: #ffffff !important;
        font-family: Manrope, sans-serif !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        letter-spacing: 0.02em !important;
        box-shadow: 0 8px 28px rgba(44, 28, 22, 0.28) !important;
        cursor: pointer !important;
        opacity: 1 !important;
        visibility: visible !important;
        animation: fjChipEntrance 0.4s ease both;
      }

      .${CHIP_CLASS}:active {
        transform: scale(0.97);
      }

      @keyframes fjChipEntrance {
        from {
          opacity: 0;
          transform: translateY(14px);
        }

        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media screen and (min-width: 750px) {
        .${CHIP_CLASS} {
          display: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function createChip() {
    if (!isMobile()) return;
    if (document.querySelector(`.${CHIP_CLASS}`)) return;

    addStyles();

    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = CHIP_CLASS;
    chip.textContent = '10% na primeira compra';
    chip.setAttribute('aria-label', 'Abrir cupom de primeira compra');

    chip.addEventListener('click', async () => {
      const popup = findCouponPopup();

      if (!popup) {
        console.error('[Fabiani] Pop-up do cupom não encontrado.');
        return;
      }

    if (typeof popup.open === 'function') {
  popup.open();
  chip.remove();

  document.dispatchEvent(
    new CustomEvent('fj:popup:opened', {
      detail: {
        source: 'mobile_chip'
      }
    })
  );

  return;
}

      await customElements.whenDefined('pop-up');

     if (typeof popup.open === 'function') {
  popup.open();
  chip.remove();

  document.dispatchEvent(
    new CustomEvent('fj:popup:opened', {
      detail: {
        source: 'mobile_chip'
      }
    })
  );
}
    });

    document.body.appendChild(chip);
  }

  function init() {
    console.log('[Fabiani] Script mobile carregado.');

    window.setTimeout(createChip, CHIP_DELAY);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
(() => {
  const CONFIG = {
    mobileMaxWidth: 749,
    minimumTimeMs: 45000,
    requiredDepth: 0.65,
    returnToTopPoint: 0.15,
    maximumReturnTimeMs: 5000
  };

  const SESSION_KEY = 'fj_popup_mobile_interacted';

  let reachedRequiredDepth = false;
  let upwardMovementStartedAt = 0;
  let upwardMovementExpired = false;
  let lastScrollY = window.scrollY;
  let ticking = false;
  const pageStartedAt = Date.now();

  function isMobile() {
    return window.matchMedia(
      `(max-width: ${CONFIG.mobileMaxWidth}px)`
    ).matches;
  }

  function isExcludedPage() {
    return (
      window.location.pathname.startsWith('/cart') ||
      window.location.pathname.startsWith('/checkout') ||
      window.location.pathname.startsWith('/checkouts')
    );
  }

  function findCouponPopup() {
    return Array.from(document.querySelectorAll('pop-up')).find((popup) =>
      popup.querySelector(
        'a[href*="popup-primeira10"], a[href*="PRIMEIRA10"]'
      )
    );
  }

  function readCookie(name) {
    return document.cookie
      .split('; ')
      .find((cookie) =>
        cookie.startsWith(`${encodeURIComponent(name)}=`)
      );
  }

  function wasDismissed(popup) {
    if (!popup || !popup.id) return false;

    return Boolean(readCookie(`${popup.id}-dismissed`));
  }

  function alreadyInteracted() {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  }

  function markAsInteracted() {
    sessionStorage.setItem(SESSION_KEY, 'true');
  }

  function removeCouponChip() {
    document.querySelector('.fj-mobile-coupon-chip')?.remove();
  }

  function openCouponPopup(source) {
    const popup = findCouponPopup();

    if (!popup) return;
    if (wasDismissed(popup)) return;
    if (alreadyInteracted()) return;
    if (typeof popup.open !== 'function') return;

    markAsInteracted();
    removeCouponChip();
    popup.open();

    document.dispatchEvent(
      new CustomEvent('fj:popup:opened', {
        detail: {
          source
        }
      })
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target.closest('.fj-mobile-coupon-chip');
      const couponButton = event.target.closest(
        'a[href*="popup-primeira10"]'
      );

      if (chip || couponButton) {
        markAsInteracted();
      }
    },
    true
  );

  function handleScroll() {
    if (ticking) return;

    ticking = true;

    window.requestAnimationFrame(() => {
      ticking = false;

      if (!isMobile()) return;
      if (isExcludedPage()) return;
      if (alreadyInteracted()) return;

      const currentScrollY = window.scrollY;
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      if (scrollableHeight <= 0) return;

      const scrollProgress = currentScrollY / scrollableHeight;
      const currentTime = Date.now();

      const movingUp = currentScrollY < lastScrollY - 8;
      const movingDown = currentScrollY > lastScrollY + 8;

      if (scrollProgress >= CONFIG.requiredDepth) {
        reachedRequiredDepth = true;
        upwardMovementExpired = false;
      }

      if (
        reachedRequiredDepth &&
        movingUp &&
        !upwardMovementStartedAt &&
        !upwardMovementExpired
      ) {
        upwardMovementStartedAt = currentTime;
      }

      if (movingDown) {
        upwardMovementStartedAt = 0;
        upwardMovementExpired = false;
      }

      if (
        upwardMovementStartedAt &&
        currentTime - upwardMovementStartedAt >
          CONFIG.maximumReturnTimeMs
      ) {
        upwardMovementStartedAt = 0;
        upwardMovementExpired = true;
      }

      const minimumTimeReached =
        currentTime - pageStartedAt >= CONFIG.minimumTimeMs;

      const rapidReturnToTop =
        upwardMovementStartedAt &&
        !upwardMovementExpired &&
        scrollProgress <= CONFIG.returnToTopPoint;

      if (
        minimumTimeReached &&
        reachedRequiredDepth &&
        rapidReturnToTop
      ) {
        openCouponPopup('mobile_return_to_top');
      }

      lastScrollY = currentScrollY;
    });
  }

  function removeChipWhenItShouldNotAppear() {
    const popup = findCouponPopup();

    if (
      alreadyInteracted() ||
      (popup && wasDismissed(popup))
    ) {
      removeCouponChip();
    }
  }

  window.addEventListener('scroll', handleScroll, {
    passive: true
  });

  new MutationObserver(removeChipWhenItShouldNotAppear).observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );
})();
(() => {
  const CONFIG = {
    debug: true,
    mobileMaxWidth: 749,
    chipSelector: '.fj-mobile-coupon-chip',
    couponButtonSelector: 'a[href*="popup-primeira10"]'
  };
    const GA4_DESTINATIONS = [
    'G-Q747TXK3JZ',
    'G-40S6P0V65J'
  ];

  function sendPopupEventToGa4(eventName, payload) {
    if (typeof window.gtag !== 'function') {
      console.warn(
        '[Fabiani GA4] Google Analytics ainda não está disponível.'
      );
      return;
    }

    window.gtag('event', eventName, {
      send_to: GA4_DESTINATIONS,

      popup_source:
        payload.forma_abertura ||
        payload.origem ||
        'nao_informada',

      coupon_code:
        payload.cupom ||
        'PRIMEIRA10',

      device_type:
        payload.dispositivo,

      page_path:
        payload.pagina,

      page_location:
        payload.endereco_pagina,

      page_title:
        payload.titulo_pagina,

      debug_mode: true
    });

    console.info(
      '[Fabiani GA4] Evento enviado:',
      eventName,
      GA4_DESTINATIONS
    );
  }

  const recentEvents = new Map();
  const observedChips = new WeakSet();

  function isMobile() {
    return window.matchMedia(
      `(max-width: ${CONFIG.mobileMaxWidth}px)`
    ).matches;
  }

  function isThemeEditor() {
    return Boolean(window.Shopify?.designMode);
  }

  function findCouponPopup() {
    return Array.from(document.querySelectorAll('pop-up')).find(
      (popup) => popup.querySelector(CONFIG.couponButtonSelector)
    );
  }

  function createEventKey(eventName, eventData) {
    return [
      eventName,
      eventData.forma_abertura || '',
      eventData.origem || '',
      eventData.cupom || ''
    ].join('|');
  }

  function publishPopupEvent(eventName, eventData = {}) {
    if (isThemeEditor()) return;

    try {
      if (
        !window.Shopify ||
        !window.Shopify.analytics ||
        typeof window.Shopify.analytics.publish !== 'function'
      ) {
        console.warn(
          '[Fabiani Pop-up] Sistema de eventos da Shopify indisponível.'
        );
        return;
      }

      const eventKey = createEventKey(eventName, eventData);
      const currentTime = Date.now();
      const lastPublication = recentEvents.get(eventKey) || 0;

      if (currentTime - lastPublication < 700) return;

      recentEvents.set(eventKey, currentTime);

      const payload = {
        ...eventData,
        dispositivo: isMobile() ? 'mobile' : 'desktop',
        pagina: window.location.pathname,
        endereco_pagina: window.location.href,
        titulo_pagina: document.title,
        origem_acesso: document.referrer || 'direto',
        horario_local: new Date().toISOString()
      };

      const result = window.Shopify.analytics.publish(
        eventName,
        payload
      );
      sendPopupEventToGa4(eventName, payload);

      if (CONFIG.debug) {
        console.info(
          '[Fabiani Pop-up] Evento publicado:',
          eventName,
          payload,
          result
        );
      }
    } catch (error) {
      console.error(
        '[Fabiani Pop-up] Erro ao publicar evento:',
        error
      );
    }
  }

  function registerChipDisplayed(chip) {
    if (observedChips.has(chip)) return;

    observedChips.add(chip);

    publishPopupEvent('fj_popup_aba_exibida', {
      origem: 'mobile_chip'
    });
  }

  function scanForChips(root = document) {
    if (
      root instanceof Element &&
      root.matches(CONFIG.chipSelector)
    ) {
      registerChipDisplayed(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      root.querySelectorAll(CONFIG.chipSelector).forEach(
        registerChipDisplayed
      );
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target.closest(CONFIG.chipSelector);

      if (chip) {
        publishPopupEvent('fj_popup_aba_clicada', {
          origem: 'mobile_chip'
        });
      }

      const couponButton = event.target.closest(
        CONFIG.couponButtonSelector
      );

      if (couponButton) {
        publishPopupEvent('fj_popup_botao_cupom_clicado', {
          cupom: 'PRIMEIRA10'
        });
      }
    },
    true
  );

  document.addEventListener('fj:popup:opened', (event) => {
    const popup = findCouponPopup();
    const source =
      event.detail?.source || 'nao_identificada';

    if (popup) {
      popup.__fjWasOpened = true;
    }

    publishPopupEvent('fj_popup_aberto', {
      forma_abertura: source
    });

    if (source === 'mobile_return_to_top') {
      publishPopupEvent(
        'fj_popup_gatilho_retorno_top_acionado',
        {
          origem: source
        }
      );
    }
  });

  document.addEventListener(
    'fj:popup:coupon-copied',
    (event) => {
      publishPopupEvent('fj_popup_cupom_copiado', {
        cupom: event.detail?.coupon || 'PRIMEIRA10'
      });
    }
  );

  document.addEventListener(
    'fj:popup:coupon-copy-error',
    (event) => {
      publishPopupEvent('fj_popup_erro_ao_copiar', {
        cupom: event.detail?.coupon || 'PRIMEIRA10',
        mensagem:
          event.detail?.message || 'erro_nao_identificado'
      });
    }
  );

  function instrumentPopup(popup) {
    if (!popup) return;
    if (popup.__fjAnalyticsInstrumented) return;
    if (!popup.querySelector(CONFIG.couponButtonSelector)) return;

    popup.__fjAnalyticsInstrumented = true;

    if (typeof popup.open === 'function') {
      const originalOpen = popup.open.bind(popup);

      popup.open = function (...args) {
        this.__fjWasOpened = true;

        const result = originalOpen(...args);

        if (!isMobile()) {
          publishPopupEvent('fj_popup_aberto', {
            forma_abertura: 'desktop_exit_intent'
          });
        }

        return result;
      };
    }

    if (typeof popup.close === 'function') {
      const originalClose = popup.close.bind(popup);

      popup.close = function (...args) {
        const shouldRegisterClose =
          this.__fjWasOpened === true;

        const result = originalClose(...args);

        if (shouldRegisterClose) {
          publishPopupEvent('fj_popup_fechado', {
            origem: isMobile()
              ? 'mobile'
              : 'desktop'
          });

          this.__fjWasOpened = false;
        }

        return result;
      };
    }
  }

  async function initializePopupInstrumentation() {
    await customElements.whenDefined('pop-up');

    document.querySelectorAll('pop-up').forEach(
      instrumentPopup
    );

    const popupObserver = new MutationObserver(
      (mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            if (node.matches('pop-up')) {
              instrumentPopup(node);
            }

            node.querySelectorAll?.('pop-up').forEach(
              instrumentPopup
            );
          });
        });
      }
    );

    popupObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initializeChipObserver() {
    scanForChips();

    const chipObserver = new MutationObserver(
      (mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              scanForChips(node);
            }
          });
        });
      }
    );

    chipObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initializeTracking() {
    initializeChipObserver();
    initializePopupInstrumentation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeTracking
    );
  } else {
    initializeTracking();
  }
})();